import moment from 'moment';
import { useMutation } from '@apollo/react-hooks';
import Typography from 'components/UI/Typography';
import Divider from 'components/UI/Divider';
import Spacer from 'components/UI/Layout/Spacer';
import StyledButton from 'components/UI/Button/StyledButton';
import { InfoButton } from 'components/PreRegister/InfoButton';
import React, { ComponentType, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { oc } from 'ts-optchain';
import dayjs from 'dayjs';
import { Alert } from 'components/Alert';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { SkipPaymentFlags } from 'lib/constants/preRegister';
import { Color } from 'modules/styles/colors';
import { formatDate } from 'modules/utils/DateUtils';
import { IconSize, FontSize, FontWeight, Spacing } from 'modules/styles/variables';
import {
  MuiBox,
  MuiCheckbox,
  MuiRadio,
  MuiPaper,
  MuiTypography,
  MuiInputLabel,
  MuiSelect,
  MuiMenuItem
} from 'theme/material-ui';
import { NavigationScreenProps } from 'screens/navigation';
import { MarginMuiBox, PaddingMuiBox, EndIcon, Term, Bold } from '../styled';
import { PreRegMakeSelectionOptions, infoDefinitions } from '../constant';
import FlexBox, { FlexBoxColumn, FlexBoxRow } from 'components/UI/Layout/FlexBox';
import { PreRegMakeSelectionOptionsType } from '../types';
import { PaymentForm, ApplyPaymentResponse } from 'store/preRegistration/payment/types';
import { submitPaymentValidationSchema } from './validationSchema';
import { CreditCardModel } from './CreditCard';
import { ACHModel } from './ACH';
import paymentFormModel from './FormConstant';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import Screen from 'components/UI/Layout/Screen';
import ConfirmModal from 'components/UI/Modals/ConfirmModal';
import formatMoney from 'services/formatMoney';
import DefaultText from 'components/UI/Typography/DefaultText';

import { RootState, RootDispatch } from 'store/types';
import {
  SkipPaymentFlagTypes,
  VisitDetail,
  EncounterInfo,
  PatientInfo
} from 'store/preRegistration/types';
import {
  ApplyPaymentFetchingSelector,
  PaymentInfoFetchingSelector,
  ApplyPaymentDataSelector,
  PaymentInfoDataSelector,
  ApplyPaymentErrorSelector
} from 'store/preRegistration/payment/selectors';
import { paymentInfo, applyPayment, resetPaymentInfo } from 'store/preRegistration/payment/actions';
import {
  PreRegistrationVisitDetailSelector,
  PreRegistrationSkipPaymentFlagsSelector,
  PreRegistrationEncounterInfoSelector,
  PreRegistrationPatientInfoSelector,
  PreRegistrationEventIdSelector,
  PreRegistrationShouldSkipActivityCompleteSelector
} from 'store/preRegistration/selectors';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import LinkingServices from 'services/LinkingServices';
import { UPDATE_COLLECTION_ATTEMPTS } from 'services/orchestration/mutations';
import { headerWithJWT } from '../utils';
import * as actions from 'store/preRegistration/actions';
import { PreRegHeader } from '../common';
import Config from 'react-native-config';
import { RedMuiBox } from 'components/Error/styled';
import { Warning } from 'components/Error/common';
import { InputField } from 'components/UI/Input/TextInput';
import { StyledAsterisk } from 'components/RequiredFieldsLegend';

const renderSpinner = () => <SpinnerModal isLoading />;
export interface Props extends NavigationScreenProps {
  dispatch: RootDispatch;
  isFetching: boolean;
  isFetchingPaymentInfo: boolean;
  paymentInfoData: null | any;
  encounterInfoData: EncounterInfo;
  patientInfoData: PatientInfo;
  receiptData: null | ApplyPaymentResponse;
  visitDetail?: VisitDetail;
  paymentInfo: () => Promise<void>;
  resetPaymentInfo: () => void;
  applyPayment: (form: PaymentForm, priorBalance: boolean, payNow: boolean) => Promise<any>;
  error: string;
  currentUrl?: string;
  referringUrl?: string;
  skipPaymentFlags: SkipPaymentFlagTypes;
  PreRegistrationEventId: string;
  shouldSkipActivity: boolean;
}

export const Payment = (props: Props) => {
  const { formField } = paymentFormModel;
  const inputLabel = React.useRef<HTMLLabelElement>(null);

  const history = useHistory();
  const dispatch = useDispatch();
  const [updateCollectionAttempts, { loading: mutationLoading }] = useMutation(
    UPDATE_COLLECTION_ATTEMPTS
  );

  const [payNow, setPayNow] = React.useState(true);
  const [priorBalance, setPriorBalance] = React.useState(false);
  const [payLaterPriorBalance, setPayLaterPriorBalance] = React.useState(false);
  const [makeSelection, setMakeSelection] = React.useState('');
  const [goToCreditCardModel, setGoToCreditCardModel] = React.useState(false);
  const [goToACHModel, setGoToACHModel] = React.useState(false);
  const [showPaymentOverview, setShowPaymentOverview] = React.useState(false);
  const [submitPaymentLoading, setSubmitPaymentLoading] = React.useState(false);
  const outOfNetwork = oc(props).visitDetail.activity.isPlanOutOfNetwork(false);
  const startDateValue = props.visitDetail?.startDate || '';
  const accountList: any[] = oc(props).paymentInfoData.accountList([]);
  const patientResponsibilityInCents = Math.abs(
    oc(props).visitDetail.payment.patientResponsibilityInCents(0)
  );
  let payNowAmount = patientResponsibilityInCents;
  if (priorBalance) {
    payNowAmount = patientResponsibilityInCents + props.paymentInfoData?.totalDueAmount;
  }
  if (Config.PRE_REG_PRIOR_BAL_PAY_LATER_FEATURE === 'enabled') {
    if (payLaterPriorBalance) {
      payNowAmount = props.paymentInfoData?.totalDueAmount;
    }
  }

  const formatZip = (zip: string) => {
    return zip.length > 5 ? zip.substring(0, 5) : zip;
  };

  const initialValues: PaymentForm = {
    paymentOption: '',
    paymentAmount: 0.0,
    paymentMethod: '1',
    cardNumber: '',
    cardExpiration: '',
    cardCvv: '',
    firstName: oc(props).patientInfoData.firstName(''),
    lastName: oc(props).patientInfoData.lastName(''),
    address1: oc(props).patientInfoData.addresses[0].address1(''),
    address2: oc(props).patientInfoData.addresses[0].address2(''),
    city: oc(props).patientInfoData.addresses[0].city(''),
    state: oc(props).patientInfoData.addresses[0].state(''),
    zipCode: formatZip(oc(props).patientInfoData.addresses[0].zip('')),
    accountType: '2',
    accountName: '',
    routingNumber: '',
    accountNumber: '',
    emailAddress: props.patientInfoData.emails?.length
      ? props.patientInfoData.emails[0].address.toLowerCase()
      : '',
    encounters: accountList.map(a => ({ ...a, paymentAmount: undefined })),
    paymentPlans: [],
    acknowledge: false
  };

  useEffect(() => {
    props.paymentInfo();
    return props.resetPaymentInfo;
  }, []);

  const sendAnalyticsEvent = () => {
    const { currentUrl, referringUrl, visitDetail, skipPaymentFlags, paymentInfoData } = props;
    const payment = visitDetail?.payment;
    const { patientResponsibilityInCents } = payment || {};
    const priorBalanceExists = paymentInfoData.totalDueAmount > 0 ? 'Yes' : 'No';
    const estimateAvailable = patientResponsibilityInCents > 0 ? 'Yes' : 'No';
    let insuranceStatus = 'Has Insurance';
    if (skipPaymentFlags[SkipPaymentFlags.SELF_PAY_ELECT]) {
      insuranceStatus = 'Self Pay Elect';
    } else if (skipPaymentFlags[SkipPaymentFlags.SELF_PAY]) {
      insuranceStatus = 'Self Pay';
    }

    let additionalInfo = `Prior Balance Exists (${priorBalanceExists}). Estimate Available (${estimateAvailable}). Insurance Status (${insuranceStatus})`;
    if (!payNow) {
      additionalInfo = `${additionalInfo}. Pay Later reason (${makeSelection})`;
    }
    const formCompleteData: AmplitudeEventData = {
      currentUrl,
      referringUrl,
      /* eslint-disable-next-line @typescript-eslint/camelcase */
      form_location: visitDetail?.location?.name || ''
    };

    const paymentCompleteData: AmplitudeEventData = {
      ...formCompleteData,
      /* eslint-disable-next-line @typescript-eslint/camelcase */
      form_detail_additional_info: additionalInfo
    };

    analyticsService.logEvent(AnalyticsEvent.HospitalFormComplete, formCompleteData);
    analyticsService.logEvent(AnalyticsEvent.HospitalFormPaymentComplete, paymentCompleteData);
  };

  const sendCollectionAttemptCall = async (
    costEstimateAmount: number,
    priorBalanceAmount: number
  ) => {
    const generateReasonCode = PreRegMakeSelectionOptions.find(a => a.label === makeSelection)
      ?.code;

    let reasonCode = 1;
    let amountSent = 0;

    if (generateReasonCode) {
      reasonCode = generateReasonCode;
    }
    if (costEstimateAmount) {
      amountSent = costEstimateAmount;
    }

    // send collection attempt API
    const mutationPayload = {
      finNumber: props?.visitDetail?.id,
      facilityCode: props?.visitDetail?.facilityCode,
      transactionDate: moment().format(),
      paymentModeType: amountSent || priorBalanceAmount ? 'CreditCard' : 'Non-Collection',
      deviceType: 'Mobile',
      paymentModeReasonId: reasonCode,
      costEstimateAmount: amountSent,
      priorBalanceAmount
    };

    const res = await updateCollectionAttempts({
      variables: {
        updateCollectionAttemptsInput: mutationPayload
      },
      context: {
        clientName: 'orchestration',
        header: headerWithJWT()
      }
    });

    if (res.data.updateCollectionAttempts.success) {
      dispatch({
        type: actions.UPDATE_COLLECTION_ATTEMPT_STATUS
      });
      dispatch({
        type: actions.UPDATE_PROCESS_STATUS,
        payload: res.data.updateCollectionAttempts.success
      });

      // finally sent to complete pre-reg
      setSubmitPaymentLoading(false);
      sendAnalyticsEvent();
      history.push(`/pre-reg/checklist`);
    } else {
      setSubmitPaymentLoading(false);
      Alert.alert('Failure', 'Payment not submitted correctly', [
        { text: 'OK', onPress: () => {} }
      ]);
    }
  };

  const submitPayment = async (values: PaymentForm) => {
    setSubmitPaymentLoading(true);
    values.paymentAmount = payNowAmount;
    let response = null;
    try {
      // Dispatch apply payment action only if no receipt was already created
      if (oc(props).receiptData.receiptNumber() === undefined) {
        response = await props.applyPayment(values, priorBalance, payNow);
      }
      if (response?.payload?.data?.receiptNumber) {
        const costEstimateAmount = payNow ? patientResponsibilityInCents : 0;
        const priorBalanceAmount = priorBalance ? props.paymentInfoData.totalDueAmount : 0;
        sendCollectionAttemptCall(costEstimateAmount, priorBalanceAmount);
      }
    } catch (err) {
      let errorMessage = 'Unknown error. Please try later';
      if (response?.error?.response?.data?.resolution) {
        errorMessage = response?.error?.response?.data?.resolution;
      }
      setSubmitPaymentLoading(false);
      Alert.alert('Error Submitting Payment', errorMessage);
    }
  };

  const runGoToCreditCardModel = (values: PaymentForm) => {
    // setting paymentMethod as CC
    values.paymentMethod = '1';
    // Clear ACH data
    values.accountName = '';
    values.routingNumber = '';
    values.accountNumber = '';

    setGoToCreditCardModel(true);
  };

  const runGoToACHModel = (values: PaymentForm) => {
    // setting paymentMethod as ACH
    values.paymentMethod = '2';
    // Clear CC data
    values.cardNumber = '';
    values.cardExpiration = '';
    values.cardCvv = '';

    setGoToACHModel(true);
  };

  const handleSelect = (value: string) => {
    setMakeSelection(value);
  };

  const togglePaymentOverviewModal = () => {
    if (!payNow && !payLaterPriorBalance) {
      sendCollectionAttemptCall(0, 0);
    } else {
      setShowPaymentOverview(!showPaymentOverview);
    }
  };

  const handleRequestOpen = () => {
    Alert.alert(
      'Terms You Should Know',
      null,
      undefined,
      { cancelable: true, closeIcon: true },
      () => {
        return (
          <div>
            {infoDefinitions.map(def => {
              return (
                <Term key={def.Term}>
                  {def.Term ? <Bold>{def.Term}:</Bold> : null} {def.Definition}
                </Term>
              );
            })}
          </div>
        );
      }
    );
  };

  const renderPaymentInfo = (values: PaymentForm) => (
    <>
      <MarginMuiBox>
        <MuiBox display="flexbox" m={2} flexDirection="column" alignItems="center">
          <Typography textAlign="center" fontSize={FontSize.medium}>
            Pay Now:
          </Typography>
          <Spacer size="small" />
          <Typography textAlign="center" fontSize={FontSize.heading} fontWeight={FontWeight.bold}>
            {`$${formatMoney(payNowAmount)}`}
          </Typography>
        </MuiBox>
        <Spacer size="small" />
        <Typography fontSize={FontSize.medium} fontWeight={FontWeight.bold}>
          Payment Method
        </Typography>
        <Typography fontSize={FontSize.small}>
          You will be charged the day of your appointment.
        </Typography>
      </MarginMuiBox>
      <Spacer size="small" />
      <Divider />
      <MarginMuiBox
        pt={2}
        pb={2}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        onClick={() => runGoToCreditCardModel(values)}
      >
        <Typography fontWeight={values.cardNumber ? FontWeight.bold : FontWeight.normal}>
          {values.cardNumber
            ? `Card ending in *${values.cardNumber.substring(values.cardNumber.length - 4)}`
            : 'Pay with Debit or Credit Card'}
        </Typography>
        <EndIcon name="keyboard-arrow-right" size={IconSize.base} />
      </MarginMuiBox>
      <Divider />
      <MarginMuiBox
        pt={2}
        pb={2}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        onClick={() => runGoToACHModel(values)}
      >
        <Typography fontWeight={values.accountNumber ? FontWeight.bold : FontWeight.normal}>
          {values.accountNumber
            ? `Account ending in *${values.accountNumber.substring(
                values.accountNumber.length - 4
              )}`
            : 'Pay with ACH withdrawal'}
        </Typography>
        <EndIcon name="keyboard-arrow-right" size={IconSize.base} />
      </MarginMuiBox>
      <MarginMuiBox>
        <MuiTypography fontSize={FontSize.medium}>
          Email Receipt to <StyledAsterisk />
        </MuiTypography>
        <InputField
          name={formField.emailAddress.name}
          label={formField.emailAddress.label}
          fullWidth
        />
        <Spacer size="small" />
      </MarginMuiBox>
      <Divider />
    </>
  );

  const renderPayNow = (values: PaymentForm) => (
    <>
      {props?.paymentInfoData?.totalDueAmount ? (
        <MarginMuiBox px={1} display="flex" flexDirection="row" justifyContent="space-between">
          <MuiBox display="flex" flexDirection="row" alignItems="center">
            <MuiCheckbox
              checked={priorBalance}
              onChange={() => setPriorBalance(!priorBalance)}
              value={priorBalance}
              color="primary"
              inputProps={{
                'aria-label': 'primary checkbox'
              }}
            />
            <Typography textAlign="center">Add my prior balance</Typography>
          </MuiBox>
          <Typography textAlign="center" fontWeight={FontWeight.bold}>
            {props.paymentInfoData ? `$${formatMoney(props.paymentInfoData.totalDueAmount)}` : 0}
          </Typography>
        </MarginMuiBox>
      ) : null}

      {renderPaymentInfo(values)}
    </>
  );

  const renderPayLater = (values: PaymentForm) => (
    <>
      <Spacer size="small" />
      <MarginMuiBox>
        <MuiTypography>
          Reason <StyledAsterisk />
        </MuiTypography>
      </MarginMuiBox>
      <MarginMuiBox>
        <MuiInputLabel id="pre-reg-make-selection-label" ref={inputLabel}>
          Make selection
        </MuiInputLabel>
        <MuiSelect
          fullWidth
          id="pre-reg-make-selection"
          labelId="pre-reg-make-selection-label"
          label="Make selection"
          value={makeSelection}
          onChange={event => handleSelect(event.target.value)}
        >
          {PreRegMakeSelectionOptions.map((option: PreRegMakeSelectionOptionsType) => (
            <MuiMenuItem style={{ whiteSpace: 'normal' }} key={option.label} value={option.label}>
              {option.label}
            </MuiMenuItem>
          ))}
        </MuiSelect>
      </MarginMuiBox>
      {Config.PRE_REG_PRIOR_BAL_PAY_LATER_FEATURE === 'enabled' &&
      props?.paymentInfoData?.totalDueAmount ? (
        <>
          <MarginMuiBox
            pr={1}
            pl={1}
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <MuiBox display="flex" flexDirection="row" alignItems="center">
              <MuiCheckbox
                checked={payLaterPriorBalance}
                onChange={() => setPayLaterPriorBalance(!payLaterPriorBalance)}
                value={payLaterPriorBalance}
                color="primary"
                inputProps={{
                  'aria-label': 'primary checkbox'
                }}
              />
              <Typography textAlign="center">Pay my prior balance</Typography>
            </MuiBox>
            <Typography textAlign="center" fontWeight={FontWeight.bold}>
              {props.paymentInfoData ? `$${formatMoney(props.paymentInfoData.totalDueAmount)}` : 0}
            </Typography>
          </MarginMuiBox>
          {payLaterPriorBalance ? renderPaymentInfo(values) : null}
        </>
      ) : null}
    </>
  );

  if (props.isFetchingPaymentInfo) {
    return (
      <Screen safe>
        <SpinnerModal isLoading />
      </Screen>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={submitPaymentValidationSchema}
      onSubmit={submitPayment}
    >
      {({ values, isValid, handleSubmit, setFieldValue, errors, touched }) => {
        return (
          <>
            <MuiBox flexDirection="column" alignContent="center">
              <PreRegHeader title="Payment" />

              <MarginMuiBox pt={2} pb={2}>
                <Typography fontSize={FontSize.large} fontWeight={FontWeight.bold}>
                  Please confirm how you'd like to pay for your balance.
                </Typography>
              </MarginMuiBox>
              <Divider />
              <MarginMuiBox>
                <Typography fontWeight={FontWeight.bold}>
                  Estimated Visit Costs
                  <InfoButton
                    handleRequestOpen={handleRequestOpen}
                    hideBorder
                    color={Color.blueHue2}
                  />
                </Typography>
              </MarginMuiBox>
              <MarginMuiBox p={1} display="flex" flexDirection="row" justifyContent="space-between">
                <Typography>Visit Date</Typography>
                <Typography>{dayjs(startDateValue).format('MM/DD/YYYY')}</Typography>
              </MarginMuiBox>
              <MarginMuiBox p={1} display="flex" flexDirection="row" justifyContent="space-between">
                <Typography>Your estimated cost for this service:</Typography>
                <Typography fontWeight={FontWeight.bold}>
                  {patientResponsibilityInCents
                    ? `$${formatMoney(patientResponsibilityInCents)}`
                    : 0}
                </Typography>
              </MarginMuiBox>
              <Spacer size="small" />
              {outOfNetwork ? (
                <FlexBoxRow data-testID="insurance-out-of-network">
                  <FlexBox flex="1 1 auto">
                    <Warning />
                  </FlexBox>
                  <FlexBox
                    flex="1 1 auto"
                    padding={`0px ${Spacing.large}px ${Spacing.large}px 0px`}
                  >
                    <DefaultText fontSize={FontSize.small} fontWeight="bold">
                      Out-of-Network
                    </DefaultText>
                    <DefaultText fontSize={FontSize.small}>
                      Your insurance is out-of-network for your visit location and may cost
                      significantly more than at an in-network location.
                    </DefaultText>
                    <Spacer size="small" />
                  </FlexBox>
                </FlexBoxRow>
              ) : null}
              <Divider />
              <Spacer size="small" />
              <MarginMuiBox flexDirection="column">
                <Typography fontWeight={FontWeight.bold}>Choose Payment Option</Typography>
                <MuiPaper>
                  <PaddingMuiBox
                    display="flex"
                    onClick={() => {
                      setPayNow(!payNow);
                      setPriorBalance(false);
                      if (Config.PRE_REG_PRIOR_BAL_PAY_LATER_FEATURE === 'enabled') {
                        setPayLaterPriorBalance(false);
                      }
                    }}
                  >
                    <MuiBox flex="1">
                      <MuiRadio checked={payNow} value={payNow} color="primary" />
                    </MuiBox>
                    <MuiBox flex="4" flexDirection="column">
                      <Typography>Pay Visit</Typography>
                      <Typography>Estimate Now</Typography>
                    </MuiBox>
                    <MuiBox flex="2" alignSelf="center">
                      <Typography fontWeight={FontWeight.bold} textAlign="right">
                        {patientResponsibilityInCents
                          ? `$${formatMoney(patientResponsibilityInCents)}`
                          : 0}
                      </Typography>
                    </MuiBox>
                  </PaddingMuiBox>
                </MuiPaper>
                <Spacer size="small" />
                <MuiPaper>
                  <PaddingMuiBox
                    display="flex"
                    onClick={() => {
                      setPayNow(!payNow);
                    }}
                  >
                    <MuiBox flex="1">
                      <MuiRadio checked={!payNow} value={!payNow} color="primary" />
                    </MuiBox>
                    <MuiBox flex="4" flexDirection="column">
                      <Typography>Pay Visit</Typography>
                      <Typography>Cost Later</Typography>
                    </MuiBox>
                    <MuiBox flex="2" alignSelf="center">
                      <Typography fontWeight={FontWeight.bold} textAlign="right">
                        {patientResponsibilityInCents
                          ? `$${formatMoney(patientResponsibilityInCents)}`
                          : 0}
                      </Typography>
                    </MuiBox>
                  </PaddingMuiBox>
                </MuiPaper>
              </MarginMuiBox>

              {payNow ? renderPayNow(values) : renderPayLater(values)}
              <MarginMuiBox pt={2} pb={2}>
                <Typography fontWeight={FontWeight.bold}>Acknowledge</Typography>
                <MuiBox display="flex" flexDirection="row" alignItems="center">
                  <MuiCheckbox
                    checked={values.acknowledge}
                    color="primary"
                    onChange={() => {
                      setFieldValue('acknowledge', !values.acknowledge);
                    }}
                    value={values.acknowledge}
                    inputProps={{
                      'aria-label': 'primary checkbox'
                    }}
                  />
                  <MuiBox pt={2}>
                    <MuiTypography>
                      I acknowledge this is{' '}
                      <MuiTypography display="inline" fontWeight={FontWeight.bold}>
                        only an estimate of costs
                      </MuiTypography>{' '}
                      and that I may receive additional bills
                    </MuiTypography>
                  </MuiBox>
                </MuiBox>
                <MuiBox ml={5} onClick={handleRequestOpen}>
                  <MuiTypography color={Color.link}>Learn more</MuiTypography>
                </MuiBox>
              </MarginMuiBox>
              {Object.keys(touched).length && Object.keys(errors).length ? (
                <RedMuiBox m={3} mt={1} mb={1} p={1}>
                  <FlexBoxRow data-testID="please-check-your-information" alignItems="center">
                    <FlexBox>
                      <Warning />
                    </FlexBox>
                    <FlexBox alignItems="center">
                      <DefaultText fontSize={FontSize.base} fontWeight="bold">
                        Please complete following information
                      </DefaultText>
                    </FlexBox>
                  </FlexBoxRow>
                  <MuiBox m={1}>
                    {Object.keys(errors).map(keys =>
                      errors[keys] ? (
                        <>
                          <DefaultText fontSize={FontSize.base}>{`● ${errors[keys]}`}</DefaultText>
                          <br />
                        </>
                      ) : null
                    )}
                  </MuiBox>
                </RedMuiBox>
              ) : null}
              <MarginMuiBox>
                {mutationLoading ? (
                  renderSpinner()
                ) : (
                  <StyledButton
                    onPress={togglePaymentOverviewModal}
                    disabled={
                      // INFO: pay now check form validation and acknownledge, pay later check make selection and acknowledge
                      payNow || payLaterPriorBalance
                        ? !values.acknowledge || !isValid
                        : !values.acknowledge || !!!makeSelection.length
                    }
                  >
                    {payNow || payLaterPriorBalance ? 'Submit Payment' : 'Submit'}
                  </StyledButton>
                )}
              </MarginMuiBox>
              <Spacer size="small" />
              <MuiBox display="flexbox" m={2} flexDirection="column" alignItems="center">
                <Typography textAlign="center" fontSize={FontSize.medium}>
                  If you have any questions. Please call
                </Typography>
                <Typography
                  textAlign="center"
                  color={Color.link}
                  fontWeight={FontWeight.bold}
                  onClick={() => LinkingServices.callPhoneNumber('18882698674')}
                >
                  888-269-8674
                </Typography>
              </MuiBox>
              <Spacer size="large" />
              <ConfirmModal
                accessibilityLabel="remove card modal"
                isOpen={showPaymentOverview}
                title="Payment Overview"
                position="bottom"
                icon=""
                fullWidth
                isLoading={submitPaymentLoading}
                onCancel={() => setShowPaymentOverview(false)}
                subTitleElement={() => {
                  return (
                    <FlexBoxColumn width="100%">
                      <FlexBoxRow justifyContent="space-between">
                        <Typography>Payment amount: </Typography>
                        <Typography>${formatMoney(payNowAmount || '0')}</Typography>
                      </FlexBoxRow>
                      <FlexBoxRow justifyContent="space-between">
                        <Typography>Scheduled date: </Typography>
                        <Typography>{`${formatDate(startDateValue)}`}</Typography>
                      </FlexBoxRow>
                      <FlexBoxRow justifyContent="space-between">
                        <Typography>Payment type: </Typography>
                        <Typography>{values.cardNumber ? `Card` : `ACH`}</Typography>
                      </FlexBoxRow>
                    </FlexBoxColumn>
                  );
                }}
                onConfirm={handleSubmit}
              />
            </MuiBox>

            {goToCreditCardModel && (
              <CreditCardModel
                closePopup={setGoToCreditCardModel}
                formField={formField}
                values={values}
                isValid={isValid}
                setFieldValue={setFieldValue}
              />
            )}
            {goToACHModel && (
              <ACHModel
                closePopup={setGoToACHModel}
                formField={formField}
                values={values}
                isValid={isValid}
                setFieldValue={setFieldValue}
              />
            )}
          </>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => ({
  paymentInfoData: PaymentInfoDataSelector(state),
  encounterInfoData: PreRegistrationEncounterInfoSelector(state),
  patientInfoData: PreRegistrationPatientInfoSelector(state),
  receiptData: ApplyPaymentDataSelector(state),
  error: ApplyPaymentErrorSelector(state),
  isFetching: ApplyPaymentFetchingSelector(state),
  isFetchingPaymentInfo: PaymentInfoFetchingSelector(state),
  visitDetail: PreRegistrationVisitDetailSelector(state),
  PreRegistrationEventId: PreRegistrationEventIdSelector(state),
  currentUrl: currentLocationPathNameSelector(state),
  referringUrl: previousLocationPathNameSelector(state),
  skipPaymentFlags: PreRegistrationSkipPaymentFlagsSelector(state),
  shouldSkipActivity: PreRegistrationShouldSkipActivityCompleteSelector(state)
});

const mapDispatchToProps = {
  paymentInfo,
  applyPayment,
  resetPaymentInfo
};

export default connect(mapStateToProps, mapDispatchToProps)(Payment as ComponentType);
